/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */


import apiClient from './client';
import { useAuthStore } from '@/store/authStore';
import type { PluginFileDTO, UploadResponse, SyncResult } from './plugins';

export type { PluginFileDTO, UploadResultItem, UploadResponse, SyncResult, SyncResultItem } from './plugins';

/** A GPS telematics provider currently loaded by the host (Plugins/Gps). */
export interface LoadedGpsPlugin {
  providerId: string;
}

/** DLL-management API for the GPS plugin family. Mirrors pluginsApi (toll) against /api/gpsplugins. */
export const gpsPluginsApi = {
  async list(): Promise<PluginFileDTO[]> {
    const res = await apiClient.get<PluginFileDTO[]>('/gpsplugins');
    return res.data;
  },

  /** Providers currently loaded by the host (for the settings tab). */
  async listLoaded(): Promise<LoadedGpsPlugin[]> {
    const res = await apiClient.get<LoadedGpsPlugin[]>('/gpsplugins/loaded');
    return res.data;
  },

  async upload(files: File[]): Promise<UploadResponse> {
    const form = new FormData();
    files.forEach((f) => form.append('files', f, f.name));
    const res = await apiClient.post<UploadResponse>('/gpsplugins/upload', form, { timeout: 120000 });
    return res.data;
  },

  async delete(fileName: string): Promise<void> {
    await apiClient.delete(`/gpsplugins/${encodeURIComponent(fileName)}`);
  },

  async rescan(): Promise<void> {
    await apiClient.post('/gpsplugins/rescan');
  },

  /** Pull GPS plugin DLLs from PluginBinaries onto disk and hot-reload. */
  async syncFromDb(): Promise<SyncResult> {
    const res = await apiClient.post<SyncResult>('/gpsplugins/sync-from-db', undefined, { timeout: 120000 });
    return res.data;
  },

  async download(fileName: string): Promise<void> {
    const token = useAuthStore.getState().token;
    const res = await fetch(`/api/gpsplugins/download/${encodeURIComponent(fileName)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed: ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
